import clsx from 'clsx'
import { ArrowLeftIcon } from 'lucide-react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { Banner, Button } from 'slab'
import { useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansDetails as useGetMedicalPlansDetails } from 'utils/web_api'

import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { PageErrorBanner } from '../components/PageErrorBanner'
import { MedicalDetailPlanTable } from '../components/PlanTable/DetailPlanTable'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'
import { clearPlansForComparison } from '../utils/planComparisonStorage'

export function MedicalPlansComparePage ({
  navigateToNextStep,
  navigateToSpecificStep,
  navigateToPreviousStep,
  metadata,
}: StepComponentProps) {
  const { enrollmentEventId } = useOnboarding()
  const planIds = metadata?.planIds

  const {
    data: plans,
    isLoading: isLoadingPlan,
    error: medicalPlanError,
  } = useGetMedicalPlansDetails(
    enrollmentEventId,
    {
      'plan_ids[]': planIds,
    },
    {
      query: {
        retry: false,
        queryKey: [
          'enrollmentEvent',
          enrollmentEventId,
          'medical_plans',
          planIds,
        ],
      },
    }
  )

  if (medicalPlanError) {
    return (
      <div className='p-10'>
        <PageErrorBanner error={medicalPlanError as GrapeAPIErrorType} />
      </div>
    )
  }

  const planDisplayItems = plans?.data

  if (isLoadingPlan && !plans) {
    return <div />
  }
  if (
    !isLoadingPlan &&
    (!plans || !planDisplayItems || planDisplayItems.length === 0)
  ) {
    return (
      <>
        <div className='flex-shrink-0 py-4 px-4'>
          <Button
            leftIcon={ArrowLeftIcon}
            size='large'
            variant='tertiary-deemphasized'
            onClick={() => {
              clearPlansForComparison()
              navigateToPreviousStep?.()
            }}
          >
            Back to all medical plans
          </Button>
        </div>
        <Banner title='Plans not found' type='critical'>
          <OnboardingGenericErrorMessage />
        </Banner>
      </>
    )
  }

  return (
    <div
      className='flex flex-col h-svh xs:h-screen isolate'
      data-testid='MedicalPlansComparePage'
    >
      <div className='flex-shrink-0 py-4 px-4'>
        <Button
          leftIcon={ArrowLeftIcon}
          size='large'
          variant='tertiary-deemphasized'
          onClick={() => {
            navigateToPreviousStep?.()
          }}
        >
          Back to all medical plans
        </Button>
      </div>
      <div className='flex-1 overflow-auto overscroll-x-none'>
        <div
          className={clsx('mx-auto', {
            'max-w-[60rem] min-w-[30rem]': planDisplayItems?.length === 1,
            'max-w-[80rem] min-w-[50rem]': planDisplayItems?.length === 2,
            'max-w-[100rem] min-w-[80rem]': planDisplayItems?.length === 3,
          })}
        >
          <MedicalDetailPlanTable
            planDisplayItems={planDisplayItems!}
            forComparison
            navigateToNextStep={navigateToNextStep}
            navigateToSpecificStep={navigateToSpecificStep}
          />
        </div>
      </div>
    </div>
  )
}
